import "../Components/RenderChatBotResponse.css";
import CustomBarChart from "../../../../Components/Chart/CustomBarChart";
import LineChartExample from "../../../../Components/Chart/CustomelineChart";
import CustomePieChart, {
  CustomLegend,
  prepareLegendData,
} from "../../../../Components/Chart/CustomePieChart";
import DoubleLineChart from "../../../../Components/Chart/DoubleLineChart";
import { motion } from "framer-motion";
import {
  containerVariants,
  chartVariants,
  titleVariants,
  legendVariants,
  AnimatedChartContainer,
} from "./chartAnimation";
import shareChartIcon from "../../../../assets/imgs/share-chart-icon.svg";
import { useRef, useState, useMemo } from "react";
import { useChartShare } from "../../../../hooks/useChartShare";
import { hapticsImpactLight } from "../Services";
import { useMixpanel } from "../../../../Context/MixpanelContext/MixpanelProvider";
import ChatbotImg from "../../../../assets/imgs/rotobot_chart.svg";

export default function ChartRender({ chartResponse }: any) {
  const chartRef = useRef<HTMLDivElement>(null);
  const shareContainerRef = useRef<HTMLDivElement>(null);
  const { shareChart } = useChartShare();
  const Mixpanel = useMixpanel();

  const handleShareChart = async () => {
    try {
      Mixpanel.trackEvent("chart_shared", {
        name: "User clicked Share Chart button",
      });
      hapticsImpactLight();
      // Use the share container ref instead of the regular chart ref
      await shareChart(shareContainerRef, chartResponse?.chart_title);
    } catch (error) {
      console.error("Failed to share chart:", error);
    }
  };

  // Transform x_values and y_values into the format {label: x, value: y}
  const transformData = () => {
    // Special case for line_double chart type
    if (
      chartResponse.chart_type === "line_double" &&
      chartResponse?.x_values &&
      chartResponse?.y_series_values
    ) {
      return chartResponse.x_values.map((label: string, index: number) => ({
        label,
        value1: chartResponse.y_series_values[0]?.[index] ?? null,
        value2: chartResponse.y_series_values[1]?.[index] ?? null,
      }));
    }

    // Standard case for other chart types
    if (chartResponse?.x_values && chartResponse?.y_values) {
      return chartResponse.x_values.map((label: string, index: number) => ({
        label,
        value: chartResponse.y_values[index],
      }));
    }
    return chartResponse?.data_pairs || [];
  };

  const transformedData = transformData();
  const isPieChart = chartResponse.chart_type === "pie";

  // Prepare legend data for pie chart
  const legendData = isPieChart ? prepareLegendData(transformedData) : null;

  // Conditionally render chart based on chart_type
  const renderChart = (image?: boolean) => {
    switch (chartResponse.chart_type) {
      case "bar":
        return (
          <AnimatedChartContainer>
            <CustomBarChart
              data={transformedData}
              {...(image && {
                xAxisTitle: chartResponse?.x_axis_title,
                yAxisTitle: chartResponse?.y_axis_title,
              })}
            />
          </AnimatedChartContainer>
        );
      case "line":
        return (
          <AnimatedChartContainer>
            <LineChartExample
              data={transformedData}
              {...(image && {
                xAxisTitle: chartResponse?.x_axis_title,
                yAxisTitle: chartResponse?.y_axis_title,
              })}
            />
          </AnimatedChartContainer>
        );
      case "pie":
        return (
          <AnimatedChartContainer>
            <CustomePieChart data={transformedData} />
          </AnimatedChartContainer>
        );
      case "line_double":
        return (
          <AnimatedChartContainer>
            <DoubleLineChart
              data={transformedData}
              series_labels={chartResponse?.series_labels}
              {...(image && {
                xAxisTitle: chartResponse?.x_axis_title,
                yAxisTitle: chartResponse?.y_axis_title,
              })}
            />
          </AnimatedChartContainer>
        );
      default:
        return (
          <AnimatedChartContainer>
            <CustomBarChart
              data={transformedData}
              {...(image && {
                xAxisTitle: chartResponse?.x_axis_title,
                yAxisTitle: chartResponse?.y_axis_title,
              })}
            />
          </AnimatedChartContainer>
        );
    }
  };

  const getContainerClass = () => {
    if (chartResponse.chart_type === "pie") {
      return "h-[270px] mt-[8px] mx-[-20px]";
    }
    return "h-[300px] mt-[20px]  mx-[-20px]";
  };

  return (
    <>
      {/* Regular chart display for UI */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div ref={chartRef} className="bg-[#000513]">
          {chartResponse?.chart_title && (
            <motion.span
              className="text-white text-[14px] font-semibold mt-8 flex w-full text-center justify-center"
              variants={titleVariants}
            >
              {chartResponse.chart_title}
            </motion.span>
          )}
          <div className={getContainerClass()}>{renderChart()}</div>
          {isPieChart && legendData && (
            <motion.div
              className="mb-[15px] pie-chart-legend"
              variants={legendVariants}
              style={{
                paddingTop: "10px",
                paddingBottom: "10px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CustomLegend payload={legendData} />
            </motion.div>
          )}
        </div>

        <motion.div
          className="w-full flex items-center justify-center  mb-[25px]"
          variants={legendVariants}
        >
          <motion.button
            className="bg-[#00FFD01A] rounded-[20px] h-[42.48px] flex items-center justify-center gap-[10px] w-full disabled:opacity-50"
            onClick={handleShareChart}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div>
              <span className="text-[#04BFDA] text-[11.8px] font-semibold">
                {"Share Chart"}
              </span>
            </div>
            <div>
              <img src={shareChartIcon} alt="shareChartIcon" />
            </div>
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Hidden share container with 1200x630 dimensions - only used for sharing */}
      <div
        ref={shareContainerRef}
        className="fixed -top-[10000px] left-0 pointer-events-none"
        style={{
          width: "1200px",
          minHeight: "630px",
          backgroundColor: "#000513",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        {/* Top row with RotoBot AI label aligned to right */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
            marginTop: "30px",
            paddingRight: "40px",
          }}
        >
          <img src={ChatbotImg} alt="ChatbotImg" />
        </div>

        {/* Main content container - centered and takes remaining space */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // justifyContent: "center",
            padding: "30px 50px",
          }}
        >
          {/* Chart title */}
          {chartResponse?.chart_title && (
            <div
              className="text-white text-[25px] font-semibold mt-8 flex w-full text-center justify-center"
              style={{ marginBottom: "28px" }}
            >
              {chartResponse.chart_title}
            </div>
          )}

          {/* Chart container with proper sizing for share */}
          <div
            style={{
              width: "100%",
              height: isPieChart ? "270px" : "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: isPieChart ? "8px" : "20px",
            }}
          >
            {renderChart(true)}
          </div>

          {/* Legend for pie chart */}
          {isPieChart && legendData && (
            <div
              style={{
                marginTop: "10px",
                paddingBottom: "10px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CustomLegend payload={legendData} isImage={true} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
